import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaidIcon from '@mui/icons-material/Paid';
import ExtensionIcon from '@mui/icons-material/Extension';
import BusinessIcon from '@mui/icons-material/Business';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import { usePlatformAuth } from '../contexts/PlatformAuthContext';

const drawerWidth = 240;

export function AdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [menuOpen, setMenuOpen] = useState(false);
  const { admin, logout } = usePlatformAuth();

  useEffect(() => {
    setMenuOpen(isDesktop);
  }, [isDesktop]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const toggleMenu = () => {
    setMenuOpen((open) => !open);
  };

  const closeMenu = () => {
    if (!isDesktop) {
      setMenuOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ overflow: 'auto' }}>
      <List>
        <ListItemButton
          component={NavLink}
          to="/"
          end
          onClick={closeMenu}
          sx={{ '&.active': { bgcolor: 'action.selected' } }}
        >
          <DashboardIcon sx={{ mr: 1, fontSize: 20 }} />
          <ListItemText primary={t('nav.dashboard')} />
        </ListItemButton>
        <ListItemButton
          component={NavLink}
          to="/plans"
          onClick={closeMenu}
          sx={{ '&.active': { bgcolor: 'action.selected' } }}
        >
          <PaidIcon sx={{ mr: 1, fontSize: 20 }} />
          <ListItemText primary={t('nav.plans')} />
        </ListItemButton>
        <ListItemButton
          component={NavLink}
          to="/addons"
          onClick={closeMenu}
          sx={{ '&.active': { bgcolor: 'action.selected' } }}
        >
          <ExtensionIcon sx={{ mr: 1, fontSize: 20 }} />
          <ListItemText primary={t('nav.addons')} />
        </ListItemButton>
        <ListItemButton
          component={NavLink}
          to="/organizations"
          onClick={closeMenu}
          sx={{ '&.active': { bgcolor: 'action.selected' } }}
        >
          <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
          <ListItemText primary={t('nav.organizations')} />
        </ListItemButton>
        <ListItemButton
          component={NavLink}
          to="/renders"
          onClick={closeMenu}
          sx={{ '&.active': { bgcolor: 'action.selected' } }}
        >
          <PhotoCameraIcon sx={{ mr: 1, fontSize: 20 }} />
          <ListItemText primary={t('nav.renders')} />
        </ListItemButton>
        <ListItemButton
          component={NavLink}
          to="/settings"
          onClick={closeMenu}
          sx={{ '&.active': { bgcolor: 'action.selected' } }}
        >
          <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
          <ListItemText primary={t('nav.settings')} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <AppBar position="fixed" sx={{ zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1, minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2 } }}>
          <IconButton color="inherit" edge="start" onClick={toggleMenu} aria-label={t('nav.toggleMenu')}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('app.title')}
          </Typography>
          {admin?.email && (
            <Typography
              variant="body2"
              noWrap
              sx={{
                display: { xs: 'none', sm: 'block' },
                maxWidth: { sm: 180, md: 280 },
                opacity: 0.9,
              }}
            >
              {admin.email}
            </Typography>
          )}
          <Button color="inherit" onClick={handleLogout} size="small" sx={{ flexShrink: 0 }}>
            {t('nav.logout')}
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: menuOpen ? drawerWidth : 0 }, flexShrink: { md: 0 } }}
        aria-label={t('nav.menu')}
      >
        <Drawer
          variant={isDesktop ? 'persistent' : 'temporary'}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar />
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: 'background.default',
          p: { xs: 2, sm: 3 },
          width: { md: menuOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Container maxWidth="lg" disableGutters sx={{ px: { xs: 0, sm: 0 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
